import type { ReadItem, ReadResult } from './read'

/**
 * A slide that is only text (a carousel of tips, a numbered list, a long quote typed on a plain background) is text, not a picture
 * (docs/screenshot-cleanup/PLAN.md, decision 21). Shown as pictures, such slides were cut into crops of text, their words were left out of
 * the post (a picture says them), and they ran in screenshot order, which a share can reverse. A crop is a text slide when the reading
 * copied its words into the post's text and either it carries a lot of words or the item has several such slides; a single short quote
 * card ("You only overthink…", "Emerson") stays the picture it is. A screen of the same item marked as a picture with no crop of its
 * own is then a slide of that series too, not a screenshot to show whole.
 */
const LONG_SLIDE_WORDS = 40
const SLIDE_WORDS = 12

const bare = (text: string) => text.normalize('NFKD').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')
const wordCount = (lines: readonly string[]) => lines.join(' ').split(/\s+/).filter(Boolean).length

/** Whether most of a crop's lines are in the post's own text, as copied by the reading. */
function inText(media: ReadItem['media'][number], text: string): boolean {
  const lines = (media.words ?? []).map(bare).filter(line => line.length >= 4)
  if (!lines.length) return false
  const body = bare(text)
  return lines.filter(line => body.includes(line)).length / lines.length >= 0.6
}

export function textSlides(item: ReadItem): Set<number> {
  const copied = item.media.filter(media => wordCount(media.words ?? []) >= SLIDE_WORDS && inText(media, item.text))
  const slides = copied.length >= 2 ? copied : copied.filter(media => wordCount(media.words ?? []) >= LONG_SLIDE_WORDS)
  return new Set(slides.map(media => media.image))
}

/** The reading with text slides shown as the text they are: out of each item's crops, and their screens out of the pictures. */
export function withoutTextSlides(result: ReadResult): ReadResult {
  const notPictures = new Set<number>()
  const items = result.items.map(item => {
    const slides = textSlides(item)
    if (!slides.size) return item
    const media = item.media.filter(entry => !slides.has(entry.image))
    const kept = new Set(media.map(entry => entry.image))
    for (const image of item.images.length ? item.images : result.pictures ?? []) if (!kept.has(image)) notPictures.add(image)
    return { ...item, media }
  })
  return notPictures.size ? { ...result, items, pictures: (result.pictures ?? []).filter(image => !notPictures.has(image)) } : { ...result, items }
}
